// Stable availability grouping across the full collection, before pagination.
// Liquidacion can flag its own-brand products; available stock always wins.
// Product cards remain rendered by the store's existing Shopify section.
export function availabilityPage(products, page, pageSize) {
  if (!Number.isInteger(page) || page < 1 || !Number.isInteger(pageSize) || pageSize < 1) {
    throw new Error('Invalid collection page');
  }
  const ids = new Set();
  const rows = products.map((product, nativeIndex) => {
    const id = String(product.id);
    if (ids.has(id) || typeof product.available !== 'boolean') throw new Error('Invalid collection index');
    ids.add(id);
    return { id, available: product.available, preferredBrand: product.preferredBrand === true, nativeIndex, nativePage: Math.floor(nativeIndex / pageSize) + 1 };
  });
  const available = rows.filter((product) => product.available);
  const ordered = available.filter((product) => product.preferredBrand)
    .concat(available.filter((product) => !product.preferredBrand), rows.filter((product) => !product.available));
  const items = ordered.slice((page - 1) * pageSize, page * pageSize);
  return { items, total: rows.length, available: available.length, nativePages: [...new Set(items.map((x) => x.nativePage))] };
}

if (typeof window !== 'undefined' && window.customElements && !customElements.get('dn-availability-first')) {
  class DnAvailabilityFirst extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      queueMicrotask(() => this.renderAvailableFirst());
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    async getDocument(url) {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: this.controller.signal });
      if (!response.ok) throw new Error(`Collection response ${response.status}`);
      return response;
    }

    async getIndex(baseURL) {
      const readPage = async (page) => {
        const url = new URL(baseURL);
        url.searchParams.delete('section_id');
        url.searchParams.delete('sections');
        url.searchParams.set('view', 'dn-availability');
        url.searchParams.set(this.dataset.pageParam || 'page', String(page));
        const result = await (await this.getDocument(url)).json();
        if (result.version !== 1 || result.page !== page || !Array.isArray(result.products)) {
          throw new Error('Collection index response mismatch');
        }
        return result;
      };
      const first = await readPage(1);
      if (first.pages > 100) throw new Error('Collection exceeds Shopify pagination limit');
      const pages = [first];
      for (let start = 2; start <= first.pages; start += 4) {
        pages.push(...await Promise.all(Array.from({ length: Math.min(4, first.pages - start + 1) }, (_, i) => readPage(start + i))));
      }
      const products = pages.flatMap((page) => page.products);
      if (products.length !== first.total || pages.some((page) => page.total !== first.total)) {
        throw new Error('Collection changed during indexing');
      }
      return products;
    }

    cardsFrom(root) {
      return Array.from(root.querySelectorAll('#product-grid > li[data-dn-product-id]'));
    }

    async renderAvailableFirst() {
      const container = this.closest('#ProductGridContainer');
      const grid = container?.querySelector('ul#product-grid');
      if (!grid || !this.isConnected) { this.setAttribute('data-ready', 'empty'); return; }
      container.setAttribute('aria-busy', 'true');
      const timeout = setTimeout(() => this.controller.abort(), 15000);
      try {
        const baseURL = new URL(window.location.href);
        const nativePage = Number(this.dataset.page);
        const pageSize = Number(this.dataset.pageSize);
        const products = await this.getIndex(baseURL);
        const plan = availabilityPage(products, nativePage, pageSize);
        const cards = new Map(this.cardsFrom(container).map((card) => [card.dataset.dnProductId, card]));
        // Facet navigation can reuse cached HTML. Refresh even the current
        // native page when its availability differs from the live index.
        const needed = [...new Set(plan.items.filter((item) => {
          const card = cards.get(item.id);
          return !card || card.dataset.dnAvailable !== String(item.available);
        }).map((item) => item.nativePage))];
        for (let start = 0; start < needed.length; start += 4) {
          await Promise.all(needed.slice(start, start + 4).map(async (page) => {
            const url = new URL(baseURL);
            url.searchParams.delete('sections');
            url.searchParams.set('section_id', this.dataset.section);
            url.searchParams.set(this.dataset.pageParam || 'page', String(page));
            const html = await (await this.getDocument(url)).text();
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            for (const card of this.cardsFrom(parsed)) cards.set(card.dataset.dnProductId, card);
          }));
        }
        const orderedCards = plan.items.map((item) => {
          const card = cards.get(item.id);
          if (!card || card.dataset.dnAvailable !== String(item.available)) throw new Error('Product availability changed while loading');
          card.classList.add('scroll-trigger--cancel');
          card.classList.remove('scroll-trigger--offscreen');
          return card;
        });
        if (!this.isConnected || this.controller.signal.aborted) return;
        grid.replaceChildren(...orderedCards);
        container.dataset.dnAvailabilitySorted = 'true';
        container.dataset.dnAvailableCount = String(plan.available);
        container.dataset.dnTotal = String(plan.total);
        this.setAttribute('data-ready', 'sorted');
        document.dispatchEvent(new CustomEvent('dn:availability-sorted', { detail: { page: nativePage, total: plan.total, available: plan.available } }));
      } catch (error) {
        if (this.isConnected) {
          this.setAttribute('data-ready', 'fallback');
          container.dataset.dnAvailabilitySorted = 'fallback';
          console.warn('[Dellanatura] Availability ordering could not refresh:', error.message);
        }
      } finally {
        clearTimeout(timeout);
        if (this.isConnected) container.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('dn-availability-first', DnAvailabilityFirst);
}
